export type SectionKey =
  | "overview"
  | "cashBalance"
  | "profitability"
  | "revenueAnalysis"
  | "revenueBreakdown"
  | "topCustomers"
  | "spendAnalysis"
  | "expenseBreakdown"
  | "topVendors"
  | "accountsReceivable"
  | "arAging"
  | "accountsPayable"
  | "apAging";

export const GROUP_KEYS = {
  cashHealth: "cashHealth",
  profitLoss: "profitLoss",
  revenue: "revenue",
  expense: "expense",
  ar: "ar",
  ap: "ap",
} as const;

export type GroupKey = (typeof GROUP_KEYS)[keyof typeof GROUP_KEYS];

export type SectionGroup = {
  key: GroupKey;
  label: string;
  sections: { key: SectionKey; label: string }[];
};

export const SECTION_GROUPS: SectionGroup[] = [
  {
    key: GROUP_KEYS.cashHealth,
    label: "Cash Health",
    sections: [
      { key: "overview", label: "Overview" },
      { key: "cashBalance", label: "Cash Movement" },
    ],
  },
  {
    key: GROUP_KEYS.profitLoss,
    label: "Profit & Loss",
    sections: [{ key: "profitability", label: "Profitability" }],
  },
  {
    key: GROUP_KEYS.revenue,
    label: "Revenue",
    sections: [
      { key: "revenueAnalysis", label: "Revenue Analysis" },
      { key: "revenueBreakdown", label: "MoM Revenue Breakdown" },
      { key: "topCustomers", label: "Top Customers" },
    ],
  },
  {
    key: GROUP_KEYS.expense,
    label: "Expense",
    sections: [
      { key: "spendAnalysis", label: "Spend Analysis" },
      { key: "expenseBreakdown", label: "MoM Expense Breakdown" },
      { key: "topVendors", label: "Top Vendors" },
    ],
  },
  {
    key: GROUP_KEYS.ar,
    label: "Accounts Receivable",
    sections: [
      { key: "accountsReceivable", label: "AR Analysis" },
      { key: "arAging", label: "AR Aging" },
    ],
  },
  {
    key: GROUP_KEYS.ap,
    label: "Accounts Payable",
    sections: [
      { key: "accountsPayable", label: "AP Analysis" },
      { key: "apAging", label: "AP Aging" },
    ],
  },
];

export const DEFAULT_GROUP_ORDER = SECTION_GROUPS.map((g) => g.key);

export const DEFAULT_VISIBLE_SECTIONS: Record<SectionKey, boolean> =
  Object.fromEntries(
    SECTION_GROUPS.flatMap((g) => g.sections.map((s) => [s.key, true]))
  ) as Record<SectionKey, boolean>;

export const SECTION_OPTIONS = SECTION_GROUPS.flatMap((g) => g.sections);

export const ALL_SECTION_KEYS: SectionKey[] = SECTION_GROUPS.flatMap((g) =>
  g.sections.map((s) => s.key)
);

export const FORECAST_VISIBLE_SECTION_KEYS: SectionKey[] = [
  "profitability",
  "revenueBreakdown",
  "expenseBreakdown",
];

export const FORECAST_SECTION_GROUPS: SectionGroup[] = SECTION_GROUPS.map(
  (group) => ({
    ...group,
    sections: group.sections.filter((section) =>
      FORECAST_VISIBLE_SECTION_KEYS.includes(section.key)
    ),
  })
).filter((group) => group.sections.length > 0);

export const DEFAULT_FORECAST_GROUP_ORDER = FORECAST_SECTION_GROUPS.map(
  (g) => g.key
);

export const DEFAULT_FORECAST_VISIBLE_SECTIONS: Partial<
  Record<SectionKey, boolean>
> = Object.fromEntries(FORECAST_VISIBLE_SECTION_KEYS.map((key) => [key, true]));

export const isSectionAllowedInMode = (
  sectionKey: SectionKey,
  isForecastEnabled: boolean
): boolean =>
  !isForecastEnabled || FORECAST_VISIBLE_SECTION_KEYS.includes(sectionKey);

export const getAllowedVisibleSections = (
  visibleSections: Partial<Record<SectionKey, boolean>>,
  isForecastEnabled: boolean
): Partial<Record<SectionKey, boolean>> =>
  Object.fromEntries(
    ALL_SECTION_KEYS.filter((key) =>
      isSectionAllowedInMode(key, isForecastEnabled)
    ).map((key) => [key, Boolean(visibleSections[key])])
  );

export const getAllowedGroupOrder = (
  groupOrder: string[],
  isForecastEnabled: boolean
): string[] => {
  if (!isForecastEnabled) return groupOrder;
  const allowedGroupKeys = new Set(FORECAST_SECTION_GROUPS.map((g) => g.key));
  return groupOrder.filter((key) => allowedGroupKeys.has(key as GroupKey));
};

export const isGroupVisible = (
  groupKey: string,
  visibleSections: Partial<Record<SectionKey, boolean>>
): boolean => {
  const group = SECTION_GROUPS.find((g) => g.key === groupKey);
  if (!group) return false;
  return group.sections.some((section) =>
    Boolean(visibleSections[section.key])
  );
};

export const PERIOD_OPTIONS = [
  { value: "3_months", label: "Last 3 months" },
  { value: "6_months", label: "Last 6 months" },
  { value: "12_months", label: "Last 12 months" },
  { value: "custom", label: "Custom" },
];

export type ExportAnalysisTypes = {
  revenueAnalysisType: "CATEGORY" | "CLASSIFICATION";
  spendAnalysisType: "CATEGORY" | "CLASSIFICATION";
  arViewType: "customers" | "invoices";
  apViewType: "vendors" | "bills";
};

export const DEFAULT_EXPORT_ANALYSIS_TYPES: ExportAnalysisTypes = {
  revenueAnalysisType: "CATEGORY",
  spendAnalysisType: "CATEGORY",
  arViewType: "customers",
  apViewType: "vendors",
};

export type SectionRadioConfig = {
  analysisTypeKey: keyof ExportAnalysisTypes;
  options: { value: string; label: string }[];
};

export const SECTION_RADIO_CONFIG: Partial<
  Record<SectionKey, SectionRadioConfig>
> = {
  revenueAnalysis: {
    analysisTypeKey: "revenueAnalysisType",
    options: [
      { value: "CATEGORY", label: "Category" },
      { value: "CLASSIFICATION", label: "Classification" },
    ],
  },
  spendAnalysis: {
    analysisTypeKey: "spendAnalysisType",
    options: [
      { value: "CATEGORY", label: "Category" },
      { value: "CLASSIFICATION", label: "Classification" },
    ],
  },
  accountsReceivable: {
    analysisTypeKey: "arViewType",
    options: [
      { value: "customers", label: "Customers" },
      { value: "invoices", label: "Invoices" },
    ],
  },
  accountsPayable: {
    analysisTypeKey: "apViewType",
    options: [
      { value: "vendors", label: "Vendors" },
      { value: "bills", label: "Bills" },
    ],
  },
};

export type TrendDirection = "up" | "down";

export type TrendSubtitleProps = {
  percentage: number | null;
  period: string;
  direction: TrendDirection;
  invertColors?: boolean;
};
